'use client';

import './ShopTabNavigationModal.css';

import { Dispatch, SetStateAction, Suspense, useContext, useEffect } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useRouter, useSearchParams } from 'next/navigation';

import { ButtonProps } from '@/components/common/Button/Button';
import { CartContext } from '../../CartProvider/CartProvider';
import { DeliveryPanel } from './Delivery/DeliveryPanel';
import { Modal } from '@/components/common/Modal/Modal';
import { PickupPanel } from './Pickup/PickupPanel';
import { trackJourneyElement } from '@/utils/eventTracking';

interface ShopTabNavigationModalProps {
	redirectOnClose?: boolean;
	isModalOpen: boolean | undefined;
	openModal: Dispatch<SetStateAction<void>>;
	closeModal: Dispatch<SetStateAction<void>>;
	openButtonText?: React.ReactNode;
	disabled?: boolean; // For the Button component
	// Applies to the opening button in the event an api call needs to be made.
	apiCall?: () => Promise<unknown>;
	buttonStyle?: 'text' | 'primary' | 'secondary' | 'tertiary' | undefined;
	closeButtonText?: React.ReactNode;
	closeBtnProps?: ButtonProps;
	closeButtonType?: 'button-web' | 'button-mobile';
	closeBtnApiCall?: () => Promise<unknown>;
	footerText?: React.ReactNode;
	redirectedFrom?: string;
}

export function ShopTabNavigationModal({
	redirectOnClose = false,
	isModalOpen,
	openModal,
	closeModal,
	disabled,
	openButtonText,
	apiCall,
	buttonStyle,
	closeButtonText,
	closeBtnProps,
	closeButtonType,
	closeBtnApiCall,
	footerText,
	redirectedFrom,
}: ShopTabNavigationModalProps) {
	const { apiCart, address, firstCartFetchFinished } = useContext(CartContext);
	const searchParams = useSearchParams();
	const router = useRouter();

	const redirectToHomePage = () => router.replace(`/`);

	useEffect(() => {
		if (
			firstCartFetchFinished &&
			!(apiCart && apiCart.storeNumber) &&
			!(address.deliveryAddress || address.pickupAddress) &&
			isModalOpen === false
		) {
			// Redirecting to Home page because no address selected and there is no store loaded
			redirectToHomePage();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen]);

	const getTabIndex = () => {
		const deliveryType: string =
			redirectedFrom === 'header' ? address.type : (searchParams.get('type') ?? address.type);
		return deliveryType === 'delivery' ? 1 : 0;
	};

	const closePickupPanel = () => {
		closeModal();
	};
	const closeDeliveryPanel = () => {
		closeModal();
	};

	const betterSetOpenModal = (open: boolean) => {
		if (open) {
			openModal();
		} else {
			closeModal();
		}
	};

	const handlePickupTabClick = () => {
		trackJourneyElement({
			type: 'tab',
			action: 'select',
			location: 'pickup selection',
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});
	};

	return (
		isModalOpen !== undefined && (
			<Suspense>
				<Modal
					redirectOnClose={redirectOnClose}
					openModal={isModalOpen}
					setOpenModal={betterSetOpenModal}
					apiCall={apiCall}
					buttonStyle={buttonStyle}
					closeBtnProps={closeBtnProps}
					closeBtnApiCall={closeBtnApiCall}
					closeButtonType={closeButtonType}
					openButtonText={openButtonText}
					closeButtonText={closeButtonText}
					footerText={footerText}
					disabled={disabled}
				>
					<div className={`px-10 pb-6 pt-10`}>
						<Tabs defaultIndex={getTabIndex()}>
							<TabList>
								<Tab onClick={handlePickupTabClick}>Pick up</Tab>
								<Tab
									onClick={() => {
										window.dataLayer = window.dataLayer || [];
										window.dataLayer.push({
											event: 'journey_element',
											eventModel: {
												type: 'tab',
												action: 'click',
												location: 'shop tab navigation',
												text: 'Delivery',
												pathname: window.location.pathname,
												domain: window.location.hostname,
											},
										});
									}}
								>
									Delivery
								</Tab>
							</TabList>
							<TabPanel>
								<PickupPanel
									closeModal={closePickupPanel}
									isModalOpen={isModalOpen && getTabIndex() === 0}
									redirectedFrom={redirectedFrom}
								/>
							</TabPanel>
							<TabPanel>
								<DeliveryPanel
									closeModal={closeDeliveryPanel}
									isModalOpen={isModalOpen && getTabIndex() === 1}
									redirectedFrom={redirectedFrom}
								/>
							</TabPanel>
						</Tabs>
					</div>
				</Modal>
			</Suspense>
		)
	);
}
